import {
  Directive,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { MyPermissionsService } from '../services/my-permissions.service';

/**
 * Renders its content only when the current user holds the given permission.
 *
 *   <a *appHasPermission="'Permissions.ApiKeys.View'" routerLink="api-keys">API Keys</a>
 *
 * This is a rendering decision, not a security boundary — the server enforces the same permission
 * with [MustHavePermission], and that is what actually protects the data. Hiding a control the user
 * cannot use is a courtesy; showing it would only produce a 403.
 */
@Directive({ selector: '[appHasPermission]' })
export class HasPermissionDirective implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private required = '';
  private rendered = false;

  @Input() set appHasPermission(permission: string) {
    this.required = permission;
    this.update();
  }

  constructor(
    private templateRef: TemplateRef<unknown>,
    private viewContainer: ViewContainerRef,
    private permissions: MyPermissionsService
  ) {}

  ngOnInit(): void {
    // Re-evaluate whenever the list is (re)loaded, so a view created before the request returned
    // still appears once it does.
    this.permissions
      .changes()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.update());

    this.permissions.load().pipe(takeUntil(this.destroy$)).subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private update(): void {
    const allowed = !!this.required && this.permissions.has(this.required);

    if (allowed && !this.rendered) {
      this.viewContainer.createEmbeddedView(this.templateRef);
      this.rendered = true;
    } else if (!allowed && this.rendered) {
      this.viewContainer.clear();
      this.rendered = false;
    }
  }
}
